import { Controller } from '@hotwired/stimulus';

// Drives the templates index list: server-side filtering / sorting / search and
// infinite scroll. Keeps the segmented filter, sort control and counters in
// sync, and exchanges state with the ⌘K palette through window events.
export default class extends Controller {
  static targets = [
    'list', 'sentinel', 'footer', 'empty', 'emptyQuery',
    'segment', 'count', 'sortLabel', 'sortOption', 'dirButton',
  ];
  static values = {
    url: String, query: String, category: String, sort: String, dir: String,
  };

  connect() {
    this.page = 1;
    this.loading = false;
    this.hasMore = !this.sentinelTarget.hidden;

    this.observer = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && this.loadMore(),
      { rootMargin: '320px' },
    );
    this.observer.observe(this.sentinelTarget);

    this.onRequest = this.onRequest.bind(this);
    window.addEventListener('templates:request', this.onRequest);
  }

  disconnect() {
    this.observer?.disconnect();
    window.removeEventListener('templates:request', this.onRequest);
  }

  filterByCategory(event) {
    this.categoryValue = event.currentTarget.dataset.category;
    this.reload();
    this.broadcast();
  }

  setSort(event) {
    this.sortValue = event.currentTarget.dataset.sort;
    this.reload();
  }

  toggleDirection() {
    this.dirValue = this.dirValue === 'asc' ? 'desc' : 'asc';
    this.reload();
  }

  reset() {
    this.queryValue = '';
    this.categoryValue = 'all';
    this.reload();
    this.broadcast();
  }

  // Applied when the palette changes the query or scope.
  onRequest(event) {
    const { query, category } = event.detail || {};
    if (query !== undefined) this.queryValue = query;
    if (category !== undefined) this.categoryValue = category;
    this.reload();
  }

  broadcast() {
    window.dispatchEvent(new CustomEvent('templates:sync', {
      detail: { query: this.queryValue, category: this.categoryValue },
    }));
  }

  categoryValueChanged() { this.syncSegments(); }
  sortValueChanged() { this.syncSort(); }
  dirValueChanged() { this.syncDir(); }

  reload() {
    this.page = 1;
    this.fetch(true);
  }

  loadMore() {
    if (this.loading || !this.hasMore) return;
    this.page += 1;
    this.fetch(false);
  }

  async fetch(replace) {
    this.loading = true;
    const url = new URL(this.urlValue, window.location.origin);
    const params = url.searchParams;
    params.set('q', this.queryValue);
    params.set('category', this.categoryValue);
    params.set('sort', this.sortValue);
    params.set('dir', this.dirValue);
    params.set('page', this.page);

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (replace) this.listTarget.innerHTML = data.rows_html;
      else this.listTarget.insertAdjacentHTML('beforeend', data.rows_html);
      this.hasMore = data.has_more;
      this.updateCounts(data.counts);
      this.updateChrome(data.total);
    } finally {
      this.loading = false;
    }
  }

  updateCounts(counts) {
    this.countTargets.forEach((node) => {
      node.textContent = counts[node.dataset.countFor];
    });
  }

  updateChrome(total) {
    this.sentinelTarget.hidden = !this.hasMore;
    this.emptyTarget.hidden = total !== 0;
    if (total === 0 && this.hasEmptyQueryTarget) {
      this.emptyQueryTarget.textContent = this.queryValue;
    }
    const showFooter = !this.hasMore && total > 0;
    this.footerTarget.hidden = !showFooter;
    if (showFooter) {
      const template = this.footerTarget.dataset.footerTemplate || '';
      this.footerTarget.textContent = template.replace('COUNT', total);
    }
  }

  syncSegments() {
    this.segmentTargets.forEach((node) => {
      node.classList.toggle('is-active', node.dataset.category === this.categoryValue);
    });
  }

  syncSort() {
    this.sortOptionTargets.forEach((node) => {
      const active = node.dataset.sort === this.sortValue;
      node.classList.toggle('is-active', active);
      if (active && this.hasSortLabelTarget) {
        this.sortLabelTarget.textContent = node.textContent.trim();
      }
    });
  }

  syncDir() {
    this.dirButtonTarget.querySelectorAll('[data-dir-icon]').forEach((node) => {
      node.hidden = node.dataset.dirIcon !== this.dirValue;
    });
  }
}
